import {Money} from '@shopify/hydrogen';
import {CurrencyCode} from '@shopify/hydrogen/dist/esnext/storefront-api-types';
import {useDovetaleAffiliateLinkContext} from 'contexts/DovetaleAffiliateLinkContext.client';
import {CardStyle} from 'shared/utils/CardStyleUtil';
import {DovetaleProductLinkCard} from './DovetaleProductLinkCard';
import {DovetaleLoadingProductLinkCard} from './DovetaleLoadingProductLinkCard';
import {LinkType} from 'shared/types';
import {usePageContext} from 'contexts/PageContext.client';
import {useErrorLogger} from '@shopify/react-bugsnag';
import {useCallback} from 'react';
import {MonorailEvent, timedTrackEvent} from 'utils/monorail.client';

interface DovetaleAffiliateLinkProps {
  id: string;
  title: string;
  style: CardStyle;
}

export const DovetaleAffiliateLink = (props: DovetaleAffiliateLinkProps) => {
  const ctx = useDovetaleAffiliateLinkContext();
  const pageCtx = usePageContext();
  const logger = useErrorLogger();

  if (!ctx) {
    throw new Error('DovetaleAffiliateLinkContext not available');
  }

  const product = ctx.products.find(
    (product) => product.affiliateProductUid === props.id,
  );

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!product) return;

      try {
        await timedTrackEvent({
          event: MonorailEvent.ClickLink,
          data: {
            linkId: props.id,
            linkTitle: product.title,
            pageId: pageCtx.id,
            linkUrl: product.affiliateUrl,
            linkType: LinkType.DovetaleAffiliateLink,
          },
        });
      } catch (error) {
        logger.notify(
          new Error(
            `event tracking error: ${MonorailEvent.ClickLink}: ${error}`,
          ),
        );
      }

      window.open(product.affiliateUrl);
    },
    [props, product, pageCtx, logger],
  );

  if (!ctx.loaded) {
    return <DovetaleLoadingProductLinkCard style={props.style} />;
  }

  if (!product) {
    return null;
  }

  const price = product.variants.edges[0].node.priceV2;

  return (
    <DovetaleProductLinkCard
      title={props.title}
      style={props.style}
      image={product.images?.edges?.[0]?.node?.transformedSrc}
      imageCaption="Affiliate"
      onClick={handleClick}
      price={
        <Money
          data={{
            amount: price.amount,
            currencyCode: price.currencyCode as CurrencyCode,
          }}
        />
      }
    />
  );
};
